import {
  type OptionalNumber,
  type OptionalSize,
  getFrameSize,
  getIsSmallerThan,
  getPassepartoutHoleSizeFromArtworkSize,
} from '@lib/size'
import { type SanityFrameBuilderProfile } from '@frame-builder/data/sanity/queries/types/profile'
import { getPassepartoutMarginSizes } from './passepartout'
import { type FrameBuilderProduct, type FrameBuilderState } from './types'

/**
 * Converts an array of numbers into frame size.
 */
export const arrayToSize = (array?: OptionalNumber[]): OptionalSize => {
  return [array?.[0], array?.[1]]
}

/**
 * Gets smallest minimum size from all mouldings.
 */
export const getMinimumProfileSize = (
  profiles: SanityFrameBuilderProfile[],
  defaultSize = 1,
) => {
  if (profiles.length === 0) {
    return defaultSize
  }

  const minimumSizes = profiles
    .map((profile) => profile.minimumSize)
    .filter(Boolean) as number[]

  if (minimumSizes.length === 0) {
    return defaultSize
  }

  return Math.min(...minimumSizes)
}

/**
 * Gets largest maximum width from all mouldings.
 */
export const getMaximumProfileWidth = (
  profiles: SanityFrameBuilderProfile[],
  defaultWidth = 1000,
) => {
  if (!profiles) {
    return defaultWidth
  }

  const maximumWidths = profiles
    .map((profile) => profile.maximumWidth)
    .filter(Boolean) as number[]

  if (maximumWidths.length === 0) {
    return defaultWidth
  }

  return Math.max(...maximumWidths)
}

/**
 * Gets largest maximum height from all mouldings.
 */
export const getMaximumProfileHeight = (
  profiles: SanityFrameBuilderProfile[],
  defaultHeight = 1000,
) => {
  if (!profiles) {
    return defaultHeight
  }

  const maximumHeights = profiles
    .map((profile) => profile.maximumHeight)
    .filter(Boolean) as number[]

  if (maximumHeights.length === 0) {
    return defaultHeight
  }

  return Math.max(...maximumHeights)
}

/**
 * Determines a size is within given limits.
 */
export const getIsSizeWithinLimits = (
  width: number,
  height: number,
  maximumWidth?: number,
  maximumHeight?: number,
) => {
  if (!maximumWidth || !maximumHeight) {
    return true
  }

  return (
    (width <= maximumWidth && height <= maximumHeight) ||
    (width <= maximumHeight && height <= maximumWidth)
  )
}

/**
 * Determines whether frame builder product size is large format.
 */
export const getIsFrameBuilderLargeFormat = (product: FrameBuilderProduct) => {
  const frameSize = getProductFrameSize(product)

  if (
    !frameSize[0] ||
    !frameSize[1] ||
    !product.glass ||
    !product.glass.frameBuilderLargeFormatSettings
  ) {
    return false
  }

  // Get large format settings from glass and compare with frame size
  const isSmallerThan = getIsSmallerThan(frameSize, [
    product.glass.frameBuilderLargeFormatSettings.dimentsion1,
    product.glass.frameBuilderLargeFormatSettings.dimentsion2,
  ])

  return !isSmallerThan
}

/**
 * Gets frame size from frame builder state.
 */
export const getStateFrameSize = (state: FrameBuilderState) => {
  const [
    passepartoutTopSize,
    passepartoutRightSize,
    passepartoutBottomSize,
    passepartoutLeftSize,
  ] = getPassepartoutMarginSizes(state.passepartout)

  return getFrameSize(
    state.artwork.width ?? 0,
    state.artwork.height ?? 0,
    passepartoutTopSize,
    passepartoutRightSize,
    passepartoutBottomSize,
    passepartoutLeftSize,
  )
}

/**
 * Calculates frame perimeter (in meters).
 */
export const getStateFramePerimeter = (state: FrameBuilderState) => {
  const frameSize = getStateFrameSize(state)

  return (frameSize[0] * 2 + frameSize[1] * 2) / 100
}

/**
 * Calculates farme area (in square meters).
 */
export const getStateFrameArea = (state: FrameBuilderState) => {
  const frameSize = getStateFrameSize(state)

  return (frameSize[0] * frameSize[1]) / (100 * 100)
}

/**
 * Gets mount cutout size.
 */
export const getStatePassepartoutHoleSize = (state: FrameBuilderState) => {
  return getPassepartoutHoleSizeFromArtworkSize(
    state.artwork.width ?? 0,
    state.artwork.height ?? 0,
  )
}

/**
 * Gets frame size from frame builder product.
 */
export const getProductFrameSize = (product: FrameBuilderProduct) => {
  const [
    passepartoutTopSize,
    passepartoutRightSize,
    passepartoutBottomSize,
    passepartoutLeftSize,
  ] = getPassepartoutMarginSizes(product.passepartout)

  return getFrameSize(
    product.artwork.width ?? 0,
    product.artwork.height ?? 0,
    passepartoutTopSize,
    passepartoutRightSize,
    passepartoutBottomSize,
    passepartoutLeftSize,
  )
}

/**
 * Calculates frame perimeter (in meters).
 */
export const getProductFramePerimeter = (product: FrameBuilderProduct) => {
  const frameSize = getProductFrameSize(product)

  return (frameSize[0] * 2 + frameSize[1] * 2) / 100
}

/**
 * Calculates farme area (in square meters).
 */
export const getProductFrameArea = (product: FrameBuilderProduct) => {
  const frameSize = getProductFrameSize(product)

  return (frameSize[0] * frameSize[1]) / (100 * 100)
}

/**
 * Gets mount cutout size from frame builder product.
 */
export const getProductPassepartoutHoleSize = (
  product: FrameBuilderProduct,
) => {
  return getPassepartoutHoleSizeFromArtworkSize(
    product.artwork.width,
    product.artwork.height,
  )
}
